import React, { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, Menu, message, Tooltip } from 'antd';

import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../context/complexDocumentContext';
import { updateTemplateInsert } from '../../../utils/services/insertsApi';
import { INSERTS_TAB } from './translationValues';
import { DropdownDetails, InsertWrapper } from './styles';
import { CONTEXT_KEYS, useArtboardContext } from '../../../context/complexArtboardContext';
import { handleThumbnailError, INSERT_NAMESPACES } from '../../../utils/insertsHelper';
import { InsertPreviewModal } from './InsertPreviewModal';
import { InsertDeleteModal } from './InsertDeleteModal';
import { COMPLEX_DOCUMENT_VERSION } from '../../../utils/complexDocumentConstants';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../../context/complexVersionContext';

const RenderDropDown = ({ insert, setShowDeleteModal, applyToTemplate, t }) => {
  return (
    <DropdownDetails
      id={`${insert.id}-insert`}
      className="dropdown-menu"
      onClick={e => e.stopPropagation()}
    >
      <Dropdown
        overlay={
          <Menu>
            <Menu.Item key="1" onClick={applyToTemplate}>
              {t(
                INSERTS_TAB.ADD_THIS_INSERT_TO_TEMPLATE_LABEL.key,
                INSERTS_TAB.ADD_THIS_INSERT_TO_TEMPLATE_LABEL.value,
              )}
            </Menu.Item>
            <Menu.Item key="2" onClick={() => setShowDeleteModal(true)}>
              {t(INSERTS_TAB.DELETE_LABEL.key, INSERTS_TAB.DELETE_LABEL.value)}
            </Menu.Item>
          </Menu>
        }
        trigger={['click']}
        overlayClassName="complex-elements-tab-dropdown"
      >
        <Icon type="more" />
      </Dropdown>
    </DropdownDetails>
  );
};

const RenderInsert = ({
  insert,
  organizationInserts,
  onSuccess,
  templateId,
  templateInserts = [],
  store,
  editable,
}) => {
  const { name, thumbnail } = insert;

  const { t } = useTranslation(INSERT_NAMESPACES);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [, updateArtboard] = useArtboardContext(store => null, '');

  const lifecycleSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle, updateVersionStore] = useVersionContext(lifecycleSelector, `ReuseInsertTab`);

  const applyToTemplate = async () => {
    const payload = [
      {
        id: insert.id,
        orderNumber: templateInserts.length + 1,
      },
    ];
    try {
      const response = await updateTemplateInsert(templateId, payload);
      if (response?.statusCode === 133 && response?.data?.length) {
        const resInsert = response?.data[0];
        const tempInsert = organizationInserts.find(({ id }) => id === resInsert.id);
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS]: [
            ...templateInserts,
            {
              ...tempInsert,
              relationId: resInsert.relationId,
              orderNumber: resInsert.orderNumber,
              businessLogic: undefined,
            },
          ],
        });
        updateVersionStore({
          [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
            [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date()
          })
        });
        message.success(
          t(INSERTS_TAB.INSERT_ADD_SUCCESS.key, INSERTS_TAB.INSERT_ADD_SUCCESS.value),
        );
      } else {
        message.error(t(INSERTS_TAB.INSERT_ADD_FAILURE.key, INSERTS_TAB.INSERT_ADD_FAILURE.value));
      }
    } catch (e) {
      message.error(t(INSERTS_TAB.INSERT_ADD_FAILURE.key, INSERTS_TAB.INSERT_ADD_FAILURE.value));
    }
  };

  return (
    <InsertWrapper>
      <ul style={{ padding: '0 10px' }}>
        <li
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            margin: '0px auto',
            width: '225px',
          }}
        >
          <div className="pdf-container">
            <img src={thumbnail} onError={handleThumbnailError} alt="" />

            {editable ? (
              <>
                <Tooltip
                  title={t(INSERTS_TAB.PREVIEW_LABEL.key, INSERTS_TAB.PREVIEW_LABEL.value)}
                  placement="top"
                >
                  <div className="preview-button" onClick={() => setShowPreview(true)}>
                    <Icon type="eye" />
                  </div>
                </Tooltip>
                <RenderDropDown
                  insert={insert}
                  applyToTemplate={applyToTemplate}
                  setShowDeleteModal={setShowDeleteModal}
                  t={t}
                />
              </>
            ) : null}
          </div>
          <div className="template-name">{name}</div>
        </li>
      </ul>
      <InsertPreviewModal
        visible={showPreview}
        onCancel={() => setShowPreview(false)}
        insert={insert}
      />
      {showDeleteModal ? (
        <InsertDeleteModal
          insert={insert}
          visible={showDeleteModal}
          onCancel={() => setShowDeleteModal(false)}
          onDeleteSuccess={onSuccess}
          t={t}
        />
      ) : null}
    </InsertWrapper>
  );
};

export const ReuseInsertsTab = ({ inserts, onSuccess }) => {
  const idSelector = useCallback(
    store => ({
      templateId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
      templateInserts: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.TEMPLATE_INSERTS],
    }),
    [],
  );
  const [{ templateId, templateInserts }, store] = useDocumentContext(idSelector);

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector, 'RenderInsert');

  return (
    <ul>
      {inserts.map(v => (
        <RenderInsert
          insert={v}
          onSuccess={onSuccess}
          templateId={templateId}
          organizationInserts={inserts}
          templateInserts={templateInserts}
          store={store}
          editable={editable}
        />
      ))}
    </ul>
  );
};
